<template>
  <div>
    <div class="privacy-page userpage-bg-fill userpage-bg-fill--vox bg-vox-populi bg-animation">
      <div class="d-flex justify-content-center align-items-start py-4">
        <b-col md="20" class="mx-auto app-login-box">

          <div class="modal-dialog mx-auto privacy-dialog">
            <VueElementLoading :active="showLoading" spinner="ring" color="var(--primary)" />
            <div class="modal-content">
              <div class="modal-body">
                <div class="h5 modal-title text-center">
                  <br>
                  <v-img src="/images/logo_login.png" width="100%" height="138" contain align="center"></v-img>
                  <div class="d-flex justify-content-center mt-2">
                    <b-button-group size="sm">
                      <b-button
                        :variant="locale === 'bg' ? 'primary' : 'outline-primary'"
                        @click="setLocale('bg')"
                        class="flag-button"
                        aria-label="Български"
                        title="Български"
                      >
                        <span class="flag-icon" aria-hidden="true">🇧🇬</span>
                      </b-button>
                      <b-button
                        :variant="locale === 'en' ? 'primary' : 'outline-primary'"
                        @click="setLocale('en')"
                        class="flag-button"
                        aria-label="English"
                        title="English"
                      >
                        <span class="flag-icon" aria-hidden="true">🇬🇧</span>
                      </b-button>
                    </b-button-group>
                  </div>
                  <br>
                  <h4>{{ pageTitle }}</h4>
                </div>

                <div v-if="errorMessage" class="alert alert-danger mt-3" role="alert">
                  {{ errorMessage }}
                </div>

                <div
                  v-else-if="pageHtml"
                  class="privacy-body text-left mt-3"
                  v-html="pageHtml"
                />
              </div>
            </div>
          </div>
          <div class="text-center text-white opacity-8 mt-3">Copyright &copy; Onikom Ltd</div>
        </b-col>
      </div>
    </div>
  </div>
</template>

<script>
import AppService from "../services/app.service";
import VueElementLoading from "vue-element-loading";

function detectBrowserLocale() {
  const raw = (
    typeof navigator !== "undefined" &&
    (navigator.language || navigator.userLanguage || "")
  )
    .toLowerCase()
    .trim();
  if (raw.startsWith("bg")) {
    return "bg";
  }
  return "en";
}

const UI = {
  bg: {
    defaultTitle: "Политика на поверителност",
    noContent: "Съдържанието не е налично. Моля, опитайте по-късно.",
    loadError: "Възникна грешка при зареждане на страницата.",
  },
  en: {
    defaultTitle: "Privacy policy",
    noContent: "Content is not available. Please try again later.",
    loadError: "Something went wrong while loading the page.",
  },
};

/**
 * API shape: { response: { message, results: { key_name, translations: [{ title, content }] }, status_code } } }
 */
function pageFieldsFromResponse(res, locale = "en") {
  const root = res.data;
  if (root == null || typeof root !== "object") {
    return { title: "", html: "" };
  }

  const inner = root.response != null ? root.response : root;
  const results = inner.results;
  if (
    results &&
    typeof results === "object" &&
    Array.isArray(results.translations) &&
    results.translations.length
  ) {
    const lang = locale === "bg" ? "bg" : "en";
    const byLocale = results.translations.find(
      (x) =>
        x &&
        x.content &&
        typeof x.locale === "string" &&
        x.locale.toLowerCase() === lang
    );
    const t =
      byLocale ||
      results.translations.find((x) => x && x.content) ||
      results.translations[0];
    const fallbackTitle = UI[lang] ? UI[lang].defaultTitle : UI.en.defaultTitle;
    return {
      title: (t && t.title) || results.key_name || fallbackTitle,
      html: (t && t.content) || "",
    };
  }

  if (root.error) {
    return { title: "", html: "" };
  }

  const legacy =
    root.data !== undefined &&
    typeof root.data === "object" &&
    !Array.isArray(root.data)
      ? root.data
      : root;
  if (!legacy || typeof legacy !== "object") {
    return { title: "", html: "" };
  }
  const lang = locale === "bg" ? "bg" : "en";
  const fallbackTitle = UI[lang] ? UI[lang].defaultTitle : UI.en.defaultTitle;
  const title =
    legacy.title ||
    legacy.name ||
    legacy.key_name ||
    fallbackTitle;
  const html =
    legacy.content ||
    legacy.body ||
    legacy.html ||
    legacy.description ||
    "";
  return { title, html };
}

export default {
  name: "Privacy",
  components: {
    VueElementLoading,
  },
  data() {
    const loc = detectBrowserLocale();
    return {
      showLoading: false,
      locale: loc,
      pageTitle: UI[loc].defaultTitle,
      pageHtml: "",
      errorMessage: "",
    };
  },
  created() {
    this.loadPage();
  },
  methods: {
    apiLocaleParam() {
      return this.locale === "bg" ? "bg" : "en";
    },
    uiStrings() {
      return UI[this.apiLocaleParam()] || UI.en;
    },
    setLocale(code) {
      const next = code === "bg" ? "bg" : "en";
      if (next === this.locale) {
        return;
      }
      this.locale = next;
      this.pageTitle = this.uiStrings().defaultTitle;
      this.loadPage();
    },
    apiErrorMessage(err) {
      const d = err.response && err.response.data;
      if (d && d.error && d.error.message) {
        return d.error.message;
      }
      if (typeof d === "string") {
        return d;
      }
      return err.message || this.uiStrings().loadError;
    },
    loadPage() {
      this.showLoading = true;
      this.errorMessage = "";
      this.pageHtml = "";
      const apiLocale = this.apiLocaleParam();
      AppService.getPage("privacy", apiLocale)
        .then((res) => {
          const { title, html } = pageFieldsFromResponse(res, apiLocale);
          this.pageTitle = title || this.uiStrings().defaultTitle;
          if (!html) {
            this.errorMessage = this.uiStrings().noContent;
            return;
          }
          this.pageHtml = html;
        })
        .catch((err) => {
          this.errorMessage = this.apiErrorMessage(err);
        })
        .finally(() => {
          this.showLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.privacy-dialog {
  max-width: 720px;
  margin-top: 0;
  margin-bottom: 0;
}

.privacy-body {
  padding-right: 0.25rem;
}

.flag-button {
  min-width: 46px;
}

.flag-icon {
  font-size: 1.1rem;
  line-height: 1;
}
</style>
